import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "../../../components/AppToast";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DialogBox from "../../../components/DialogBox";
import OrganizationLanding from "./OrganizationLanding";
import OrganizationWorkspace from "./OrganizationWorkspace";
import { INITIAL_ORG_FLAT, DEFAULT_MAX_ORG_LEVEL } from "./orgConstants";
import { migrateOrgFlat } from "./customerModel";
import {
  attachHierarchyMeta,
  customerFormToOrgApiPayload,
  flatRowToOrganizationCreateRequest,
  flatRowToOrganizationUpdateRequest,
  normalizeCustomerListResponse,
  normalizeGetOrganizationResponse,
  normalizeOrganizationTreeResponse,
  pickCodeAvailableResponse,
  pickOrganizationIdFromCreateResponse,
} from "./orgRemoteMappers";
import {
  checkOrganizationCodeAvailable,
  createCustomerWithPortalAdmin,
  createOrganization,
  deleteCustomerUnderOrganization,
  deleteOrganization,
  fetchOrganizationTree,
  getOrganizationById,
  listCustomersByOrganization,
  reparentOrganization,
  updateCustomerUnderOrganization,
  updateOrganization,
} from "../../../services/organization/organizationService";
import { isOrganizationApiEnabled } from "./orgApiConfig";
import "./organization-management.css";

const ORG_API_ENABLED = isOrganizationApiEnabled();

export default function OrganizationManagement() {
  const userDetail = useSelector((state) => state?.login?.userDetail);
  const tenantId = userDetail?.tenantId;
  const auditActor = userDetail
    ? [userDetail.firstName, userDetail.lastName].filter(Boolean).join(" ").trim() ||
      userDetail.email ||
      "User"
    : "";
  const [orgFlat, setOrgFlat] = useState(() =>
    ORG_API_ENABLED ? [] : migrateOrgFlat(INITIAL_ORG_FLAT)
  );

  const [screen, setScreen] = useState("list");
  const [workspaceMode, setWorkspaceMode] = useState("create");
  const [workspaceOrgId, setWorkspaceOrgId] = useState(null);
  const [deleteOrgId, setDeleteOrgId] = useState(null);

  const refreshOrganizationList = useCallback(async () => {
    if (!ORG_API_ENABLED || !tenantId) return;
    try {
      const raw = await fetchOrganizationTree(tenantId);
      const flat = normalizeOrganizationTreeResponse(raw);
      setOrgFlat(attachHierarchyMeta(flat));
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.message || "Unknown error";
      toast.error(`Could not load organizations: ${msg}`);
    }
  }, [tenantId]);

  useEffect(() => {
    if (ORG_API_ENABLED && tenantId) {
      refreshOrganizationList();
    }
  }, [tenantId, refreshOrganizationList]);

  const loadOrganizationDetail = useCallback(
    async (organizationId) => {
      const raw = await getOrganizationById(organizationId);
      return normalizeGetOrganizationResponse(raw);
    },
    []
  );

  const refreshCustomersForOrg = useCallback(async (organizationId) => {
    const raw = await listCustomersByOrganization(organizationId, 0, 500);
    const customers = normalizeCustomerListResponse(raw, organizationId);
    setOrgFlat((prev) =>
      prev.map((o) =>
        o.id === organizationId
          ? { ...o, customers, embeddedCustomer: customers[0] }
          : o
      )
    );
  }, []);

  const verifyOrganizationCodeAvailable = useCallback(
    async (organizationCode, excludeOrganizationId) => {
      if (!tenantId) return true;
      const raw = await checkOrganizationCodeAvailable(tenantId, organizationCode, excludeOrganizationId);
      return pickCodeAvailableResponse(raw);
    },
    [tenantId]
  );

  const persistOrganization = useCallback(
    async ({ mode, row }) => {
      if (mode === "create") {
        const body = flatRowToOrganizationCreateRequest(row, { tenantId });
        const res = await createOrganization(body);
        const organizationId = pickOrganizationIdFromCreateResponse(res);
        if (!organizationId) {
          throw new Error("Create organization response did not include organizationId.");
        }
        return { organizationId };
      }
      const prevRow = orgFlat.find((o) => o.id === row.id);
      if (prevRow && prevRow.parentId !== row.parentId) {
        await reparentOrganization(row.id, row.parentId ?? null);
      }
      const body = flatRowToOrganizationUpdateRequest(row, { tenantId });
      await updateOrganization(row.id, body);
      return { organizationId: row.id };
    },
    [tenantId, orgFlat]
  );

  const persistCustomer = useCallback(async ({ organizationId, isNew, payload }) => {
    const body = customerFormToOrgApiPayload(organizationId, payload);
    if (isNew) {
      const res = await createCustomerWithPortalAdmin(organizationId, body);
      if (res && typeof res === "object") {
        return { customer: res };
      }
      return {};
    }
    await updateCustomerUnderOrganization(payload.customerId, body);
    return {};
  }, []);

  const persistDeleteCustomer = useCallback(async ({ organizationId, customerId }) => {
    await deleteCustomerUnderOrganization(organizationId, customerId);
  }, []);

  const openWorkspace = (mode, orgId) => {
    setWorkspaceMode(mode);
    setWorkspaceOrgId(orgId);
    setScreen("workspace");
  };

  const handleDeleteOrg = async () => {
    if (!deleteOrgId) return;
    const hasChildren = orgFlat.some((o) => o.parentId === deleteOrgId);
    if (hasChildren) {
      toast.error("Cannot delete an organization that has child organizations. Remove or reassign children first.");
      setDeleteOrgId(null);
      return;
    }
    try {
      if (ORG_API_ENABLED) {
        await deleteOrganization(deleteOrgId);
        await refreshOrganizationList();
      } else {
        setOrgFlat((prev) => prev.filter((o) => o.id !== deleteOrgId));
      }
      toast.success("Organization removed.");
      if (workspaceOrgId === deleteOrgId) {
        setScreen("list");
        setWorkspaceOrgId(null);
      }
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.message || "Delete failed.";
      toast.error(String(msg));
    } finally {
      setDeleteOrgId(null);
    }
  };

  return (
    <div className="org-mgmt-page container-fluid px-2 px-md-3 py-3">
      {screen === "list" && (
        <div className="card shadow-lg main-card" style={{ position: "relative" }}>
          <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
            <h4 className="mb-0 h6 fw-semibold">Manage Organization</h4>
          </div>
          <div className="card-body">
            <OrganizationLanding
              orgFlat={orgFlat}
              onCreate={() => openWorkspace("create", null)}
              onView={(id) => openWorkspace("edit", id)}
              onEdit={(id) => openWorkspace("edit", id)}
              onDeleteRequest={(id) => setDeleteOrgId(id)}
              onManualRefresh={ORG_API_ENABLED ? refreshOrganizationList : undefined}
              showCreateButton={true}
              createButtonText="Add New Organization"
              createButtonIcon="bi-plus-circle"
            />
          </div>
        </div>
      )}

      {screen === "workspace" && (
        <OrganizationWorkspace
          orgFlat={orgFlat}
          setOrgFlat={setOrgFlat}
          tenantId={tenantId}
          auditActor={auditActor}
          maxHierarchyLevel={DEFAULT_MAX_ORG_LEVEL}
          mode={workspaceMode}
          workspaceOrgId={workspaceOrgId}
          setWorkspaceOrgId={setWorkspaceOrgId}
          loadOrganizationDetail={ORG_API_ENABLED ? loadOrganizationDetail : undefined}
          refreshCustomersForOrg={ORG_API_ENABLED ? refreshCustomersForOrg : undefined}
          verifyOrganizationCodeAvailable={
            ORG_API_ENABLED && tenantId ? verifyOrganizationCodeAvailable : undefined
          }
          persistOrganization={ORG_API_ENABLED ? persistOrganization : undefined}
          persistCustomer={ORG_API_ENABLED ? persistCustomer : undefined}
          persistDeleteCustomer={ORG_API_ENABLED ? persistDeleteCustomer : undefined}
          onClose={() => {
            setScreen("list");
            setWorkspaceOrgId(null);
            if (ORG_API_ENABLED && tenantId) refreshOrganizationList();
          }}
        />
      )}

      {deleteOrgId != null && (
        <DialogBox
          modalTitle="Delete organization"
          modalContent={
            <p className="mb-0">
              Delete this organization? Supplier records linked only in this session will be removed. This action cannot be undone from the UI.
            </p>
          }
          modalFooter={
            <>
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => setDeleteOrgId(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-sm btn-danger" onClick={handleDeleteOrg}>
                Delete
              </button>
            </>
          }
          onClose={() => setDeleteOrgId(null)}
        />
      )}
    </div>
  );
}
